/**
 * 生存分析（Survival Analysis）
 * Kaplan-Meier法、ログランク検定
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';

/**
 * 生存データのエントリ
 */
export interface SurvivalData {
  time: number;      // イベント発生時間
  event: boolean;    // イベント有無（true: イベント発生、false: 打ち切り）
  group: string;     // 群
}

/**
 * Kaplan-Meier生存曲線のポイント
 */
export interface SurvivalPoint {
  time: number;           // 時点
  survival: number;       // 生存率（0-1の範囲）
  atRisk: number;         // リスク集合のサイズ
  events: number;         // イベント数
}

/**
 * 群ごとのKaplan-Meier生存曲線
 */
export interface GroupSurvivalCurve {
  group: string;          // 群名
  curve: SurvivalPoint[]; // 生存曲線のポイント
  medianSurvival?: number; // 中央生存時間（生存率0.5の時点）
  n: number;              // サンプルサイズ
  events: number;         // 総イベント数
}

/**
 * 各時点での計算過程の詳細
 */
export interface LogRankStepDetail {
  time: number;              // 時点
  groupAtRisk: number[];     // 各群のリスク集合
  groupEvents: number[];    // 各群のイベント数
  totalAtRisk: number;       // 総リスク集合
  totalEvents: number;       // 総イベント数
  groupExpected: number[];  // 各群の期待イベント数
  groupVariance: number[];  // 各群の分散
  cumulativeObserved: number[];  // 累積観察イベント数
  cumulativeExpected: number[];  // 累積期待イベント数
  cumulativeVariance: number[];  // 累積分散
}

/**
 * ログランク検定の結果
 */
export interface LogRankTestResult {
  chiSquare: number;      // カイ二乗統計量
  pValue: number;         // P値
  df: number;             // 自由度（群の数 - 1）
  calculationSteps?: LogRankStepDetail[];  // 計算過程の詳細（オプション）
  groupNames?: string[];   // 群名のリスト（オプション）
  finalObserved?: number[];  // 最終的な観察イベント数（オプション）
  finalExpected?: number[]; // 最終的な期待イベント数（オプション）
  finalVariance?: number[];  // 最終的な分散（オプション）
}

/**
 * 多重比較の結果
 */
export interface MultipleComparisonResult {
  comparison: string;     // 比較（例: "Group1 vs Group2"）
  chiSquare: number;      // カイ二乗統計量
  pValue: number;         // P値
  adjustedP?: number;     // 補正後P値（Bonferroni補正など）
}

/**
 * 生存分析の結果
 */
export interface SurvivalAnalysisResult {
  groups: GroupSurvivalCurve[];  // 群ごとの生存曲線
  logRankTest: LogRankTestResult; // ログランク検定の結果
  multipleComparisons?: MultipleComparisonResult[]; // 多重比較の結果（2群以上の場合）
}

/**
 * 標準正規分布の累積分布関数（CDF）を計算
 * Abramowitz and Stegun近似式を使用
 * 
 * @param z 標準化された値
 * @returns 累積確率 P(Z ≤ z)
 */
function normalCDF(z: number): number {
  // Abramowitz and Stegun近似式
  const a1 =  0.254829592;
  const a2 = -0.284496736;
  const a3 =  1.421413741;
  const a4 = -1.453152027;
  const a5 =  1.061405429;
  const p  =  0.3275911;

  const sign = z < 0 ? -1 : 1;
  z = Math.abs(z) / Math.sqrt(2.0);

  const t = 1.0 / (1.0 + p * z);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-z * z);

  return 0.5 * (1.0 + sign * y);
}

/**
 * カイ二乗分布のP値を計算（上側確率）
 * ログランク検定では、P(χ² ≥ 観測値) を計算する必要がある
 * 
 * @param chiSquare カイ二乗統計量
 * @param df 自由度
 * @returns P値（上側確率）
 */
function chiSquarePValue(chiSquare: number, df: number): number {
  if (df <= 0 || chiSquare < 0) {
    return 1.0;
  }
  // 観測値 0 のとき P(χ² ≥ 0) = 1（連続分布の上側確率）
  if (chiSquare === 0) {
    return 1.0;
  }
  // 自由度が1の場合
  // カイ二乗(1) = 標準正規分布(0,1)^2
  // P(χ² ≥ x) = P(|Z| ≥ √x) = 2 * (1 - Φ(√x))
  if (df === 1) {
    const z = Math.sqrt(chiSquare);
    const cdf = normalCDF(z);
    // 上側確率: P(χ² ≥ x) = 2 * (1 - Φ(√x))
    return 2 * (1 - cdf);
  }
  
  // 自由度が大きい場合（df ≥ 30）
  // カイ二乗分布は正規分布で近似可能
  // 平均 = df, 分散 = 2*df
  if (df >= 30) {
    const mean = df;
    const stdDev = Math.sqrt(2 * df);
    const z = (chiSquare - mean) / stdDev;
    const cdf = normalCDF(z);
    // 上側確率: P(χ² ≥ x) = 1 - Φ(z)
    return 1 - cdf;
  }
  
  // 小さな自由度の場合（2 ≤ df < 30）
  // Wilson-Hilferty変換を使用
  // χ² ~ N(μ, σ²) where μ = df, σ² = 2*df
  // より正確な近似: z = ((χ²/df)^(1/3) - (1 - 2/(9*df))) / sqrt(2/(9*df))
  const h = Math.pow(chiSquare / df, 1 / 3);
  const mu = 1 - 2 / (9 * df);
  const sigma = Math.sqrt(2 / (9 * df));
  const z = (h - mu) / sigma;
  const cdf = normalCDF(z);
  // 上側確率
  return 1 - cdf;
}

type SqMatrix = number[][];

const cloneSqMatrix = (matrix: SqMatrix): SqMatrix => matrix.map((row) => [...row]);

/** Gauss–Jordan 逆行列（小次元）。特異なら例外 */
function invertSqMatrix(matrix: SqMatrix): SqMatrix {
  const n = matrix.length;
  const m = cloneSqMatrix(matrix);
  const inv = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) => (i === j ? 1 : 0))
  );
  for (let i = 0; i < n; i += 1) {
    let pivotRow = i;
    let pivotValue = Math.abs(m[i]?.[i] ?? 0);
    for (let r = i + 1; r < n; r += 1) {
      const v = Math.abs(m[r]?.[i] ?? 0);
      if (v > pivotValue) {
        pivotValue = v;
        pivotRow = r;
      }
    }
    if (pivotValue < 1e-14) {
      throw new KeisukeAdapterError('singularMatrix');
    }
    if (pivotRow !== i) {
      [m[i], m[pivotRow]] = [m[pivotRow]!, m[i]!];
      [inv[i], inv[pivotRow]] = [inv[pivotRow]!, inv[i]!];
    }
    const pivot = m[i]![i]!;
    for (let j = 0; j < n; j += 1) {
      m[i]![j]! /= pivot;
      inv[i]![j]! /= pivot;
    }
    for (let r = 0; r < n; r += 1) {
      if (r === i) continue;
      const factor = m[r]![i] ?? 0;
      for (let j = 0; j < n; j += 1) {
        m[r]![j]! -= factor * (m[i]![j] ?? 0);
        inv[r]![j]! -= factor * (inv[i]![j] ?? 0);
      }
    }
  }
  return inv;
}

/**
 * k 標本ログランクのカイ二乗統計量 U' V^{-1} U
 * V は各イベント時点の多変量超幾何共分散の累積の先頭 (k-1)×(k-1) 主小行列（R survival::survdiff と同型）
 */
function logRankChiSquareFromCovariance(omega: SqMatrix, u: number[]): number {
  const k = u.length;
  if (k < 2) return 0;
  const km = k - 1;
  const sub: SqMatrix = Array.from({ length: km }, () => Array(km).fill(0));
  for (let i = 0; i < km; i += 1) {
    for (let j = 0; j < km; j += 1) {
      sub[i]![j]! = omega[i]![j] ?? 0;
    }
  }
  const v = u.slice(0, km);
  try {
    const inv = invertSqMatrix(sub);
    let chi2 = 0;
    for (let i = 0; i < km; i += 1) {
      for (let j = 0; j < km; j += 1) {
        chi2 += (v[i] ?? 0) * (inv[i]![j] ?? 0) * (v[j] ?? 0);
      }
    }
    if (!Number.isFinite(chi2) || chi2 < 0) return 0;
    return chi2;
  } catch {
    return 0;
  }
}

/**
 * Kaplan-Meier生存曲線を計算
 * 
 * @param data 生存データ
 * @returns 群ごとの生存曲線
 */
export function kaplanMeierEstimate(data: SurvivalData[]): GroupSurvivalCurve[] {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }
  
  // 群ごとにデータを分割
  const groups = new Set<string>();
  data.forEach(d => groups.add(d.group));
  
  const groupCurves: GroupSurvivalCurve[] = [];
  
  for (const group of groups) {
    const groupData = data.filter(d => d.group === group);
    
    if (groupData.length === 0) {
      continue;
    }
    
    // 時点でソート（イベントが先、打ち切りが後）
    const sortedData = [...groupData].sort((a, b) => {
      if (a.time !== b.time) {
        return a.time - b.time;
      }
      // 同じ時点では、イベントが先
      return a.event ? -1 : 1;
    });
    
    const curve: SurvivalPoint[] = [];
    let atRisk = groupData.length;
    let survival = 1.0;
    let events = 0;
    
    // 初期時点（time = 0）
    curve.push({
      time: 0,
      survival: 1.0,
      atRisk: atRisk,
      events: 0,
    });
    
    // 各時点を処理
    for (let i = 0; i < sortedData.length; i++) {
      const currentTime = sortedData[i].time;
      let eventCount = 0;
      let censorCount = 0;
      
      // 同じ時点のデータを集計
      while (i < sortedData.length && sortedData[i].time === currentTime) {
        if (sortedData[i].event) {
          eventCount++;
        } else {
          censorCount++;
        }
        i++;
      }
      i--; // ループカウンタを調整
      
      // イベントがある場合、生存率を更新
      if (eventCount > 0) {
        // Kaplan-Meier推定量: S(t) = ∏(1 - d_i / n_i)
        survival *= (1 - eventCount / atRisk);
        
        curve.push({
          time: currentTime,
          survival: survival,
          atRisk: atRisk,
          events: eventCount,
        });
      }
      
      // リスク集合を更新
      atRisk -= (eventCount + censorCount);
      events += eventCount;
      
      // リスク集合が0になったら終了
      if (atRisk <= 0) {
        break;
      }
    }
    
    // 中央生存時間を計算（生存率0.5の時点）
    let medianSurvival: number | undefined = undefined;
    for (let i = 0; i < curve.length - 1; i++) {
      if (curve[i].survival >= 0.5 && curve[i + 1].survival < 0.5) {
        // 線形補間
        const t1 = curve[i].time;
        const t2 = curve[i + 1].time;
        const s1 = curve[i].survival;
        const s2 = curve[i + 1].survival;
        if (s1 !== s2) {
          medianSurvival = t1 + (t2 - t1) * (s1 - 0.5) / (s1 - s2);
        } else {
          medianSurvival = t1;
        }
        break;
      }
    }
    
    groupCurves.push({
      group,
      curve,
      medianSurvival,
      n: groupData.length,
      events,
    });
  }
  
  return groupCurves;
}

/**
 * ログランク検定を実行
 *
 * k 標本（k≥2）で各イベント時点の多変量超幾何モデルに基づき共分散行列を累積し、
 * χ² = U' V^{-1} U（U_i = O_i − E_i、V は先頭 (k−1)×(k−1) 主小行列）を計算する。
 *
 * @param data 生存データ（元のデータから直接計算）
 * @param includeCalculationSteps 計算過程の詳細を含めるかどうか（デフォルト: false）
 * @returns ログランク検定の結果
 */
export function logRankTest(data: SurvivalData[], includeCalculationSteps: boolean = false): LogRankTestResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }
  
  // 群ごとにデータを分割
  const groups = new Set<string>();
  data.forEach(d => groups.add(d.group));
  const groupList = Array.from(groups);
  
  if (groupList.length < 2) {
    throw new KeisukeAdapterError('logRankMinTwoGroups');
  }
  
  // 各群のデータを取得
  const groupData = groupList.map(group => 
    data.filter(d => d.group === group)
  );
  
  // 全てのイベント発生時点を取得（打ち切りは除外）
  const eventTimes = new Set<number>();
  data.forEach(d => {
    if (d.event) {
      eventTimes.add(d.time);
    }
  });
  
  const sortedEventTimes = Array.from(eventTimes).sort((a, b) => a - b);
  
  // 各群の統計量を初期化
  const groupStats = groupList.map(() => ({
    observed: 0,
    expected: 0,
    variance: 0,
  }));

  const k = groupList.length;
  const omegaAccum: SqMatrix = Array.from({ length: k }, () => Array(k).fill(0));

  // 計算過程の詳細を保存する配列
  const calculationSteps: LogRankStepDetail[] = [];
  
  // 各イベント発生時点を処理
  for (const time of sortedEventTimes) {
    // 各群のリスク集合とイベント数を計算
    const groupAtRisk: number[] = [];
    const groupEvents: number[] = [];
    let totalAtRisk = 0;
    let totalEvents = 0;
    
    for (let i = 0; i < groupList.length; i++) {
      const group = groupList[i];
      const dataForGroup = groupData[i];
      
      // この時点でのリスク集合：time >= この時点のデータ数
      const atRisk = dataForGroup.filter(d => d.time >= time).length;
      
      // この時点でのイベント数：time === この時点 かつ event === true
      const events = dataForGroup.filter(d => d.time === time && d.event).length;
      
      groupAtRisk.push(atRisk);
      groupEvents.push(events);
      totalAtRisk += atRisk;
      totalEvents += events;
    }
    
    // イベントがない場合はスキップ（通常は発生しない）
    if (totalEvents === 0) {
      continue;
    }
    
    // 各群の期待イベント数と分散を計算
    const groupExpected: number[] = [];
    const groupVariance: number[] = [];
    
    for (let i = 0; i < groupList.length; i++) {
      const atRisk = groupAtRisk[i];
      const events = groupEvents[i];
      
      // 期待イベント数: e_i = (n_i * d) / n
      const expected = (atRisk * totalEvents) / totalAtRisk;
      groupStats[i].expected += expected;
      groupStats[i].observed += events;
      
      groupExpected.push(expected);
      
      // 分散の計算: V_i = (n_i * (n - n_i) * d * (n - d)) / (n^2 * (n - 1))
      let variance = 0;
      if (totalAtRisk > 1) {
        variance = (atRisk * (totalAtRisk - atRisk) * totalEvents * (totalAtRisk - totalEvents)) / (totalAtRisk * totalAtRisk * (totalAtRisk - 1));
        groupStats[i].variance += variance;
      }
      groupVariance.push(variance);
    }

    if (totalAtRisk > 1 && totalEvents > 0) {
      const c =
        (totalEvents * (totalAtRisk - totalEvents)) /
        (totalAtRisk * totalAtRisk * (totalAtRisk - 1));
      for (let gi = 0; gi < k; gi += 1) {
        const ni = groupAtRisk[gi] ?? 0;
        for (let gj = 0; gj < k; gj += 1) {
          const nj = groupAtRisk[gj] ?? 0;
          if (gi === gj) {
            omegaAccum[gi]![gj]! += ni * (totalAtRisk - ni) * c;
          } else {
            omegaAccum[gi]![gj]! -= ni * nj * c;
          }
        }
      }
    }

    // 計算過程の詳細を保存
    if (includeCalculationSteps) {
      calculationSteps.push({
        time,
        groupAtRisk: [...groupAtRisk],
        groupEvents: [...groupEvents],
        totalAtRisk,
        totalEvents,
        groupExpected: [...groupExpected],
        groupVariance: [...groupVariance],
        cumulativeObserved: groupStats.map(s => s.observed),
        cumulativeExpected: groupStats.map(s => s.expected),
        cumulativeVariance: groupStats.map(s => s.variance),
      });
    }
  }
  
  const u = groupStats.map((s) => s.observed - s.expected);
  const chiSquare = logRankChiSquareFromCovariance(omegaAccum, u);

  // 自由度 = 群の数 - 1
  const df = groupList.length - 1;
  
  // P値を計算
  const pValue = chiSquarePValue(chiSquare, df);
  
  const result: LogRankTestResult = {
    chiSquare,
    pValue,
    df,
  };
  
  // 計算過程の詳細を含める場合
  if (includeCalculationSteps) {
    result.calculationSteps = calculationSteps;
    result.groupNames = groupList;
    result.finalObserved = groupStats.map(s => s.observed);
    result.finalExpected = groupStats.map(s => s.expected);
    result.finalVariance = groupStats.map(s => s.variance);
  }
  
  return result;
}

/**
 * 多重比較（Bonferroni補正）
 * 
 * @param data 生存データ
 * @returns 多重比較の結果
 */
export function multipleComparisons(data: SurvivalData[]): MultipleComparisonResult[] {
  if (data.length === 0) {
    return [];
  }
  
  // 群ごとにデータを分割
  const groups = new Set<string>();
  data.forEach(d => groups.add(d.group));
  const groupList = Array.from(groups);
  
  if (groupList.length < 2) {
    return [];
  }
  
  const comparisons: MultipleComparisonResult[] = [];
  
  // 全ての群のペアを比較
  for (let i = 0; i < groupList.length; i++) {
    for (let j = i + 1; j < groupList.length; j++) {
      const group1 = groupList[i];
      const group2 = groupList[j];
      
      // 2群のデータを抽出
      const twoGroupData = data.filter(d => d.group === group1 || d.group === group2);
      
      // 2群のログランク検定
      const logRankResult = logRankTest(twoGroupData);
      
      comparisons.push({
        comparison: `${group1} vs ${group2}`,
        chiSquare: logRankResult.chiSquare,
        pValue: logRankResult.pValue,
      });
    }
  }
  
  // Bonferroni補正
  const numComparisons = comparisons.length;
  comparisons.forEach(comp => {
    comp.adjustedP = Math.min(1, comp.pValue * numComparisons);
  });
  
  return comparisons;
}

/**
 * 生存分析を実行
 * 
 * @param data 生存データ
 * @param includeCalculationSteps 計算過程の詳細を含めるかどうか（デフォルト: false）
 * @returns 生存分析の結果
 */
export function survivalAnalysis(data: SurvivalData[], includeCalculationSteps: boolean = false): SurvivalAnalysisResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }
  
  // Kaplan-Meier生存曲線を計算
  const curves = kaplanMeierEstimate(data);
  
  if (curves.length < 2) {
    throw new KeisukeAdapterError('survivalMinTwoGroups');
  }
  
  // ログランク検定を実行（元のデータから直接計算）
  const logRankTestResult = logRankTest(data, includeCalculationSteps);
  
  // 多重比較（2群以上の場合）
  let multipleComparisonsResult: MultipleComparisonResult[] | undefined = undefined;
  if (curves.length > 2) {
    multipleComparisonsResult = multipleComparisons(data);
  }
  
  return {
    groups: curves,
    logRankTest: logRankTestResult,
    multipleComparisons: multipleComparisonsResult,
  };
}

